import React, { useState, useEffect } from 'react';
import 'react-calendar/dist/Calendar.css';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiCalendar, FiX } from 'react-icons/fi';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Pricing = () => {
  const [roomType, setRoomType] = useState('');
  const [pricingTemplate, setPricingTemplate] = useState('');
  const [availableFactors, setAvailableFactors] = useState([]);
  const [allFactorsCache, setAllFactorsCache] = useState([]);
  const [selectedFactorNames, setSelectedFactorNames] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const navigate = useNavigate();

  // ============ ROOM TYPES (from API) ============
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(true);

  // Fetch room types from backend
  useEffect(() => {
    const fetchRoomTypes = async () => {
      setRoomTypesLoading(true);
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch room types (${response.status})`);
        }

        const data = await response.json();
        const list = Array.isArray(data?.roomtypes) ? data.roomtypes : [];

        // Extract just the names, keep order from backend
        const names = list
          .map((rt) => rt?.roomtypename)
          .filter((n) => typeof n === 'string' && n.trim() !== '');

        setRoomTypes(names);
      } catch (error) {
        console.error('Error fetching room types:', error);
        toast.error('Could not load room types');
        setRoomTypes([]);
      } finally {
        setRoomTypesLoading(false);
      }
    };

    fetchRoomTypes();
  }, []);

  // ============ PRICING TEMPLATES ============
  useEffect(() => {
    const fetchPricingTemplates = async () => {
      try {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/get-all-pricing-templates`,
          {
            method: 'GET',
            headers: { accept: 'application/json' },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch pricing templates');
        }

        const data = await response.json();
        setTemplates(data);
      } catch (error) {
        console.error('Error fetching pricing templates:', error);
      }
    };

    const fetchAllFactors = async () => {
      try {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch factors');
        }

        const factorsData = await response.json();
        setAllFactorsCache(Array.isArray(factorsData) ? factorsData : []);
      } catch (error) {
        console.error('Error fetching factors:', error);
      }
    };

    fetchPricingTemplates();
    fetchAllFactors();
  }, []);

  // ============ TEMPLATE CHANGE — resolve factor objects ============
  const handleTemplateChange = (templateId) => {
    setPricingTemplate(templateId);
    setSelectedFactorNames([]);

    if (!templateId) {
      setAvailableFactors([]);
      return;
    }

    const selectedTemplate = templates.find(
      (template) => template.templateid === parseInt(templateId)
    );

    if (!selectedTemplate || !selectedTemplate.factornames) {
      setAvailableFactors([]);
      return;
    }

    // Map each factorname in the template to its full factor object
    // so we have value_type, default_value, defaultpercentage, etc.
    const resolved = selectedTemplate.factornames
      .map((factorName) => {
        const factor = allFactorsCache.find((f) => f.factorname === factorName);
        if (!factor) return null;
        return {
          factorid: factor.factorid,
          factorname: factor.factorname,
          factor_type: factor.factor_type,
          value_type: factor.value_type || 'percentage',
          default_value: factor.default_value ?? 0,
          defaultpercentage: factor.defaultpercentage ?? 0,
          description: factor.description || '',
          isactive: factor.isactive,
        };
      })
      .filter(Boolean);

    setAvailableFactors(resolved);
  };

  const toggleFactorSelection = (factorName) => {
    setSelectedFactorNames((prev) => {
      if (prev.includes(factorName)) {
        return prev.filter((name) => name !== factorName);
      } else {
        return [...prev, factorName];
      }
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-CA');
  };

  const handleCalendarClick = () => {
    setShowDatePicker((prev) => !prev);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleApplyDates = () => {
    setShowDatePicker(false);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // ============ Helper: format factor value correctly ============
  const formatFactorValue = (factor) => {
    if (factor.value_type === 'flat') {
      const val = parseFloat(factor.default_value) || 0;
      return `₹${val}`;
    }
    // percentage
    const pct = parseFloat(factor.defaultpercentage) || 0;
    return `${pct}%`;
  };

  // ============ PRICING / DELETE / PREVIEW (unchanged) ============
  const handlePricingClick = async () => {
    if (!pricingTemplate || !startDate || !endDate) {
      toast.error('Please fill in all fields (Pricing Template and Date).');
      return;
    }

    const selectedTemplate = templates.find(
      (template) => template.templateid === parseInt(pricingTemplate)
    );
    const templateName = selectedTemplate ? selectedTemplate.templatename : '';

    if (!templateName) {
      toast.error('Selected template is invalid.');
      return;
    }

    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    try {
      if (roomType === 'All') {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price-all`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              templatename: [templateName],
              roomtypename: 'All',
              start_date: startDateStr,
              end_date: endDateStr,
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to calculate dynamic price for all room types');
        }

        const data = await response.json();
        if (data.message === 'Dynamic pricing calculation completed for all room types') {
          toast.success('Dynamic pricing calculated for all room types!');
        } else {
          toast.error('Error: Unable to calculate dynamic price for all room types.');
        }
      } else {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            templatename: [templateName],
            roomtypename: roomType,
            start_date: startDateStr,
            end_date: endDateStr,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to calculate dynamic price');
        }

        const data = await response.json();
        if (data.message === 'Dynamic pricing calculation completed') {
          toast.success('Dynamic pricing calculated successfully!');
        } else {
          toast.error('Error: Unable to calculate dynamic price.');
        }
      }
    } catch (error) {
      console.error('Error during dynamic price calculation:', error);
      toast.error('An error occurred while calculating the dynamic price.');
    }
  };

  // ============ DELETE PRICING ============
  const handleDeleteClick = async () => {
    if (!pricingTemplate || !startDate || !endDate) {
      toast.error('Please fill in all fields (Pricing Template and Date).');
      return;
    }

    const selectedTemplate = templates.find(
      (template) => template.templateid === parseInt(pricingTemplate)
    );
    const templateName = selectedTemplate ? selectedTemplate.templatename : '';

    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    try {
      if (roomType === 'All') {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/delete-dynamic-price-all`,
          {
            method: 'DELETE',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              start_date: startDateStr,
              end_date: endDateStr,
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete dynamic prices for all room types');
        }

        const data = await response.json();
        if (data.message === 'Dynamic prices deletion completed for all room types') {
          toast.success('Dynamic prices deleted for all room types!');
        } else {
          toast.error('Error: Unable to delete dynamic prices for all room types.');
        }
      } else {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/delete-dynamic-price`, {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            templatename: templateName,
            roomtypename: roomType,
            start_date: startDateStr,
            end_date: endDateStr,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to delete dynamic price');
        }

        const data = await response.json();
        if (data.message === 'Dynamic prices deletion completed') {
          toast.success('Dynamic prices deleted successfully!');
        } else {
          toast.error('Error: Unable to delete dynamic prices.');
        }
      }
    } catch (error) {
      console.error('Error during dynamic price deletion:', error);
      toast.error('An error occurred while deleting dynamic prices.');
    }
  };

  // ============ PREVIEW ============
  const handlePreviewClick = async () => {
    if (!pricingTemplate || !startDate || !endDate) {
      toast.error('Please fill in all fields (Pricing Template and Date).');
      return;
    }

    const selectedTemplate = templates.find(
      (template) => template.templateid === parseInt(pricingTemplate)
    );
    const templateName = selectedTemplate ? selectedTemplate.templatename : '';

    if (!templateName) {
      toast.error('Selected template is invalid.');
      return;
    }

    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    try {
      if (roomType === 'All') {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price-all?templatename=${encodeURIComponent(
            templateName
          )}&start_date=${startDateStr}&end_date=${endDateStr}`,
          {
            method: 'GET',
            headers: { accept: 'application/json' },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch preview data for all room types');
        }

        const data = await response.json();
        const previewDataWithTemplate = data.preview.map((roomData) => ({
          ...roomData,
          template: templateName,
        }));
        setPreviewData(previewDataWithTemplate);
      } else {
        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price?templatename=${encodeURIComponent(
            templateName
          )}&roomtypename=${encodeURIComponent(
            roomType
          )}&start_date=${startDateStr}&end_date=${endDateStr}`,
          {
            method: 'GET',
            headers: { accept: 'application/json' },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch preview data');
        }

        const data = await response.json();
        const previewDataWithTemplate = data.prices.map((priceData) => ({
          ...priceData,
          template: templateName,
        }));
        setPreviewData(previewDataWithTemplate);
      }
    } catch (error) {
      console.error('Error fetching preview data:', error);
      toast.error('An error occurred while fetching the preview data.');
    }
  };

  return (
    <div className="bg-gray-100 h-full p-6">
      {/* Header — UNCHANGED */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="btn"
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="bt"
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="btn text-black font-bold"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="btn"
          >
            Room Rates
          </button>
        </div>
      </header>

      <div className="bg-white p-6 rounded-lg flex flex-col h-[calc(100vh-180px)]">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-center text-black">Pricing Strategy</h1>
          <p className="text-xs font-poppins font-semibold text-center mb-1 text-black">
            Enhance your pricing strategy by leveraging data-driven insights to set competitive
            rates, improve profitability, and drive better occupancy across different room types.
          </p>
          <hr className="border border-gray-300 w-1/2 mx-auto mb-6" />

          {/* Form Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* ROOM TYPE (DYNAMIC) */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-black mb-2">Room Type</label>
              <select
                value={roomType}
                onChange={(e) => setRoomType(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white"
                disabled={roomTypesLoading}
              >
                <option value="">
                  {roomTypesLoading ? 'Loading room types...' : 'Select Room Type'}
                </option>

                {!roomTypesLoading &&
                  roomTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}

                {/* Always keep "All" last so it's the bulk option */}
                {!roomTypesLoading && roomTypes.length > 0 && <option value="All">All</option>}
              </select>
            </div>

            {/* PRICING TEMPLATE */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-black mb-2">Pricing Template</label>
              <select
                value={pricingTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white"
              >
                <option value="">Select Template</option>
                {templates.length === 0 ? (
                  <option>Loading templates...</option>
                ) : (
                  templates.map((template) => (
                    <option key={template.templateid} value={template.templateid}>
                      {template.templatename}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Date Picker */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-black mb-2">Select Date Range</label>
              <div className="relative">
                <button
                  onClick={handleCalendarClick}
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white text-left flex items-center justify-between ${
                    startDate && endDate ? 'text-black' : 'text-gray-500'
                  }`}
                >
                  <span>
                    {startDate && endDate
                      ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
                      : 'Select date range'}
                  </span>
                  <FiCalendar className="text-gray-400" size={16} />
                </button>

                {startDate && endDate && (
                  <button
                    onClick={handleClearDates}
                    className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>

              {showDatePicker && (
                <div className="absolute z-50 mt-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <div className="mb-4">
                    <DatePicker
                      selected={startDate}
                      onChange={handleDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      inline
                      dateFormat="MMMM d, yyyy"
                      className="react-datepicker-custom"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md mb-3">
                    <div className="text-sm">
                      <span className="font-medium text-gray-700">Selected: </span>
                      {startDate ? (
                        <span className="text-black">
                          {formatDisplayDate(startDate)}
                          {endDate ? ` to ${formatDisplayDate(endDate)}` : ' (Select end date)'}
                        </span>
                      ) : (
                        <span className="text-gray-500">Select start date</span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleClearDates}
                      className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleApplyDates}
                      className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800 transition-colors"
                      disabled={!startDate || !endDate}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ============ Factors Section — UPDATED ============ */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-black mb-4">Select Factors</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {pricingTemplate && roomType && availableFactors.length > 0 ? (
                availableFactors.map((factor) => {
                  const isSelected = selectedFactorNames.includes(factor.factorname);
                  const isFlat = factor.value_type === 'flat';

                  return (
                    <label
                      key={factor.factorname}
                      className={`flex items-start space-x-3 p-3 border rounded-md cursor-pointer transition-colors ${
                        isSelected ? 'border-black bg-black/5' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFactorSelection(factor.factorname)}
                        className="h-4 w-4 mt-0.5 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-black truncate">
                            {factor.factorname}
                          </span>
                          <span
                            className={`text-sm font-bold whitespace-nowrap ${
                              isFlat ? 'text-blue-700' : 'text-purple-700'
                            }`}
                          >
                            {formatFactorValue(factor)}
                          </span>
                        </div>

                        {factor.description && (
                          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                            {factor.description}
                          </p>
                        )}

                        <div className="flex items-center gap-1 mt-1">
                          <span
                            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
                              isFlat ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}
                          >
                            {isFlat ? 'Flat ₹' : 'Percent %'}
                          </span>
                          {factor.factor_type && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                              {factor.factor_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-500 text-sm">
                    {!pricingTemplate || !roomType
                      ? 'Please select Room Type and Pricing Template to view factors'
                      : 'No factors available for the selected room type and template.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons — UNCHANGED */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 mt-auto">
          <button
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
            onClick={handlePricingClick}
          >
            Save Pricing
          </button>
          <button
            className="px-6 py-2 bg-white text-black border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm"
            onClick={handlePreviewClick}
          >
            Preview
          </button>
          <button
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium text-sm"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Preview Modal — UNCHANGED */}
      {previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-black">
                {roomType === 'All' ? 'Pricing Preview - All Rooms' : 'Pricing Preview'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  <span className="font-medium text-black">Date Range:</span> {previewData[0].date}{' '}
                  to {previewData[previewData.length - 1].date}
                </p>
                {roomType !== 'All' && (
                  <p>
                    <span className="font-medium text-black">Room Type:</span>{' '}
                    {previewData[0].roomtypename}
                  </p>
                )}
                <p>
                  <span className="font-medium text-black">Template:</span>{' '}
                  {previewData[0].template}
                </p>
              </div>

              {roomType === 'All' ? (
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(
                    previewData.reduce((acc, roomData) => {
                      if (!acc[roomData.roomtypename]) {
                        acc[roomData.roomtypename] = {
                          roomtypename: roomData.roomtypename,
                          baseprice: roomData.baseprice,
                          dynamicprice: roomData.dynamicprice,
                        };
                      }
                      return acc;
                    }, {})
                  ).map(([roomTypeKey, roomData]) => (
                    <div
                      key={roomTypeKey}
                      className="p-3 bg-gray-50 rounded-md border border-gray-200"
                    >
                      <h3 className="text-sm font-semibold text-black mb-2">
                        {roomData.roomtypename}
                      </h3>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Base Price:</span> ₹{roomData.baseprice}
                        </p>
                        <p>
                          <span className="font-medium">Dynamic Price:</span> ₹
                          {roomData.dynamicprice}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>
                      <span className="font-medium text-black">Base Price:</span> ₹
                      {previewData[0].baseprice}
                    </p>
                    <p>
                      <span className="font-medium text-black">Dynamic Price:</span> ₹
                      {previewData[0].dynamicprice}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
                  onClick={() => setPreviewData(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer className="mt-12" />
    </div>
  );
};

export default Pricing;

// import React, { useState, useEffect } from 'react';
// import 'react-calendar/dist/Calendar.css';
// import { useNavigate } from 'react-router-dom';
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { FiCalendar, FiX } from 'react-icons/fi';

// const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

// const Pricing = () => {
//   const [roomType, setRoomType] = useState('');
//   const [pricingTemplate, setPricingTemplate] = useState('');
//   const [availableFactors, setAvailableFactors] = useState([]);
//   const [selectedFactorNames, setSelectedFactorNames] = useState([]);
//   const [templates, setTemplates] = useState([]);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [showDatePicker, setShowDatePicker] = useState(false);
//   const [previewData, setPreviewData] = useState(null);
//   const navigate = useNavigate();

//   const roomTypes = [
//     'Suite',
//     'Premium Double',
//     'Premium Single',
//     'Comfort Single',
//     'Comfort Double',
//     'All',
//   ];

//   useEffect(() => {
//     const fetchPricingTemplates = async () => {
//       try {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/get-all-pricing-templates`,
//           {
//             method: 'GET',
//             headers: {
//               accept: 'application/json',
//             },
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Failed to fetch pricing templates');
//         }

//         const data = await response.json();
//         setTemplates(data);
//       } catch (error) {
//         console.error('Error fetching pricing templates:', error);
//       }
//     };

//     fetchPricingTemplates();
//   }, []);

//   const handleTemplateChange = async (templateId) => {
//     setPricingTemplate(templateId);
//     setSelectedFactorNames([]);

//     try {
//       const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!response.ok) {
//         throw new Error('Failed to fetch factors');
//       }

//       const factorsData = await response.json();
//       const selectedTemplate = templates.find(
//         (template) => template.templateid === parseInt(templateId)
//       );

//       if (selectedTemplate) {
//         const factorsWithPercentage = selectedTemplate.factornames.map((factorName) => {
//           const factor = factorsData.find((f) => f.factorname === factorName);
//           return {
//             factorname: factorName,
//             defaultpercentage: factor ? factor.defaultpercentage : 'N/A',
//           };
//         });

//         setAvailableFactors(factorsWithPercentage);
//       } else {
//         setAvailableFactors([]);
//       }
//     } catch (error) {
//       console.error('Error fetching factors:', error);
//       setAvailableFactors([]);
//     }
//   };

//   const toggleFactorSelection = (factorName) => {
//     setSelectedFactorNames((prev) => {
//       if (prev.includes(factorName)) {
//         return prev.filter((name) => name !== factorName);
//       } else {
//         return [...prev, factorName];
//       }
//     });
//   };

//   const formatDate = (date) => {
//     if (!date) return '';
//     return date.toLocaleDateString('en-CA');
//   };

//   const handleCalendarClick = () => {
//     setShowDatePicker((prev) => !prev);
//   };

//   const handleDateChange = (dates) => {
//     const [start, end] = dates;
//     setStartDate(start);
//     setEndDate(end);
//   };

//   const handleApplyDates = () => {
//     setShowDatePicker(false);
//   };

//   const handleClearDates = () => {
//     setStartDate(null);
//     setEndDate(null);
//     setShowDatePicker(false);
//   };

//   const formatDisplayDate = (date) => {
//     if (!date) return '';
//     return date.toLocaleDateString('en-US', {
//       month: 'short',
//       day: 'numeric',
//       year: 'numeric',
//     });
//   };

//   // Rest of your functions (handlePricingClick, handleDeleteClick, handlePreviewClick) remain the same
//   const handlePricingClick = async () => {
//     if (!pricingTemplate || !startDate || !endDate) {
//       toast.error('Please fill in all fields (Pricing Template and Date).');
//       return;
//     }

//     const selectedTemplate = templates.find(
//       (template) => template.templateid === parseInt(pricingTemplate)
//     );
//     const templateName = selectedTemplate ? selectedTemplate.templatename : '';

//     if (!templateName) {
//       toast.error('Selected template is invalid.');
//       return;
//     }

//     const startDateStr = formatDate(startDate);
//     const endDateStr = formatDate(endDate);

//     try {
//       if (roomType === 'All') {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price-all`,
//           {
//             method: 'POST',
//             headers: {
//               accept: 'application/json',
//               'Content-Type': 'application/json',
//             },
//             body: JSON.stringify({
//               templatename: [templateName], // Changed to array
//               roomtypename: 'All',
//               start_date: startDateStr,
//               end_date: endDateStr,
//             }),
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Failed to calculate dynamic price for all room types');
//         }

//         const data = await response.json();
//         if (data.message === 'Dynamic pricing calculation completed for all room types') {
//           toast.success('Dynamic pricing calculated for all room types!');
//         } else {
//           toast.error('Error: Unable to calculate dynamic price for all room types.');
//         }
//       } else {
//         // For single room type, check which endpoint you're using
//         // If it's the "calculate-dynamic-price" endpoint, you might need to check its expected format
//         const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price`, {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             templatename: [templateName], // Also changed to array for consistency
//             roomtypename: roomType,
//             start_date: startDateStr,
//             end_date: endDateStr,
//           }),
//         });

//         if (!response.ok) {
//           throw new Error('Failed to calculate dynamic price');
//         }

//         const data = await response.json();
//         if (data.message === 'Dynamic pricing calculation completed') {
//           toast.success('Dynamic pricing calculated successfully!');
//         } else {
//           toast.error('Error: Unable to calculate dynamic price.');
//         }
//       }
//     } catch (error) {
//       console.error('Error during dynamic price calculation:', error);
//       toast.error('An error occurred while calculating the dynamic price.');
//     }
//   };

//   const handleDeleteClick = async () => {
//     if (!pricingTemplate || !startDate || !endDate) {
//       toast.error('Please fill in all fields (Pricing Template and Date).');
//       return;
//     }

//     const selectedTemplate = templates.find(
//       (template) => template.templateid === parseInt(pricingTemplate)
//     );
//     const templateName = selectedTemplate ? selectedTemplate.templatename : '';

//     const startDateStr = formatDate(startDate);
//     const endDateStr = formatDate(endDate);

//     try {
//       if (roomType === 'All') {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/delete-dynamic-price-all`,
//           {
//             method: 'DELETE',
//             headers: {
//               accept: 'application/json',
//               'Content-Type': 'application/json',
//             },
//             body: JSON.stringify({
//               start_date: startDateStr,
//               end_date: endDateStr,
//             }),
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Failed to delete dynamic prices for all room types');
//         }

//         const data = await response.json();
//         if (data.message === 'Dynamic prices deletion completed for all room types') {
//           toast.success('Dynamic prices deleted for all room types!');
//         } else {
//           toast.error('Error: Unable to delete dynamic prices for all room types.');
//         }
//       } else {
//         const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/delete-dynamic-price`, {
//           method: 'DELETE',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             templatename: templateName,
//             roomtypename: roomType,
//             start_date: startDateStr,
//             end_date: endDateStr,
//           }),
//         });

//         if (!response.ok) {
//           throw new Error('Failed to delete dynamic price');
//         }

//         const data = await response.json();
//         if (data.message === 'Dynamic prices deletion completed') {
//           toast.success('Dynamic prices deleted successfully!');
//         } else {
//           toast.error('Error: Unable to delete dynamic prices.');
//         }
//       }
//     } catch (error) {
//       console.error('Error during dynamic price deletion:', error);
//       toast.error('An error occurred while deleting dynamic prices.');
//     }
//   };

//   const handlePreviewClick = async () => {
//     if (!pricingTemplate || !startDate || !endDate) {
//       toast.error('Please fill in all fields (Pricing Template and Date).');
//       return;
//     }

//     const selectedTemplate = templates.find(
//       (template) => template.templateid === parseInt(pricingTemplate)
//     );
//     const templateName = selectedTemplate ? selectedTemplate.templatename : '';

//     if (!templateName) {
//       toast.error('Selected template is invalid.');
//       return;
//     }

//     const startDateStr = formatDate(startDate);
//     const endDateStr = formatDate(endDate);

//     try {
//       if (roomType === 'All') {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price-all?templatename=${encodeURIComponent(
//             templateName
//           )}&start_date=${startDateStr}&end_date=${endDateStr}`,
//           {
//             method: 'GET',
//             headers: {
//               accept: 'application/json',
//             },
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Failed to fetch preview data for all room types');
//         }

//         const data = await response.json();
//         const previewDataWithTemplate = data.preview.map((roomData) => ({
//           ...roomData,
//           template: templateName,
//         }));
//         setPreviewData(previewDataWithTemplate);
//       } else {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/calculate-dynamic-price?templatename=${encodeURIComponent(
//             templateName
//           )}&roomtypename=${encodeURIComponent(
//             roomType
//           )}&start_date=${startDateStr}&end_date=${endDateStr}`,
//           {
//             method: 'GET',
//             headers: {
//               accept: 'application/json',
//             },
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Failed to fetch preview data');
//         }

//         const data = await response.json();
//         const previewDataWithTemplate = data.prices.map((priceData) => ({
//           ...priceData,
//           template: templateName,
//         }));
//         setPreviewData(previewDataWithTemplate);
//       }
//     } catch (error) {
//       console.error('Error fetching preview data:', error);
//       toast.error('An error occurred while fetching the preview data.');
//     }
//   };

//   return (
//     <div className="bg-gray-100 h-full p-6">
//       {/* Header */}
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
//             className="btn"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
//             className="btn"
//           >
//             Templates
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
//             className="bt"
//           >
//             Factors
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
//             className="btn text-black font-bold"
//           >
//             Pricing
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
//             className="btn"
//           >
//             Room Rates
//           </button>
//         </div>
//       </header>

//       <div className="bg-white p-6 rounded-lg flex flex-col h-[calc(100vh-180px)]">
//         <div className="flex-1">
//           <h1 className="text-xl font-bold text-center text-black">Pricing Strategy</h1>
//           <p className="text-xs font-poppins font-semibold text-center mb-1 text-black">
//             Enhance your pricing strategy by leveraging data-driven insights to set competitive
//             rates, improve profitability, and drive better occupancy across different room types.
//           </p>
//           <hr className="border border-gray-300 w-1/2 mx-auto mb-6" />

//           {/* Form Grid */}
//           <div className="grid md:grid-cols-3 gap-6 mb-8">
//             <div className="flex flex-col">
//               <label className="text-sm font-medium text-black mb-2">Room Type</label>
//               <select
//                 value={roomType}
//                 onChange={(e) => setRoomType(e.target.value)}
//                 className="border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white"
//               >
//                 <option value="">Select Room Type</option>
//                 {roomTypes.map((type) => (
//                   <option key={type} value={type}>
//                     {type}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="flex flex-col">
//               <label className="text-sm font-medium text-black mb-2">Pricing Template</label>
//               <select
//                 value={pricingTemplate}
//                 onChange={(e) => handleTemplateChange(e.target.value)}
//                 className="border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white"
//               >
//                 <option value="">Select Template</option>
//                 {templates.length === 0 ? (
//                   <option>Loading templates...</option>
//                 ) : (
//                   templates.map((template) => (
//                     <option key={template.templateid} value={template.templateid}>
//                       {template.templatename}
//                     </option>
//                   ))
//                 )}
//               </select>
//             </div>

//             {/* Improved Date Picker */}
//             <div className="flex flex-col">
//               <label className="text-sm font-medium text-black mb-2">Select Date Range</label>
//               <div className="relative">
//                 <button
//                   onClick={handleCalendarClick}
//                   className={`w-full border border-gray-300 rounded-md px-3 py-2 outline-none text-sm bg-white text-left flex items-center justify-between ${
//                     startDate && endDate ? 'text-black' : 'text-gray-500'
//                   }`}
//                 >
//                   <span>
//                     {startDate && endDate
//                       ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
//                       : 'Select date range'}
//                   </span>
//                   <FiCalendar className="text-gray-400" size={16} />
//                 </button>

//                 {startDate && endDate && (
//                   <button
//                     onClick={handleClearDates}
//                     className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
//                   >
//                     <FiX size={16} />
//                   </button>
//                 )}
//               </div>

//               {/* Date Picker Popup */}
//               {showDatePicker && (
//                 <div className="absolute z-50 mt-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
//                   <div className="mb-4">
//                     <DatePicker
//                       selected={startDate}
//                       onChange={handleDateChange}
//                       startDate={startDate}
//                       endDate={endDate}
//                       selectsRange
//                       inline
//                       dateFormat="MMMM d, yyyy"
//                       className="react-datepicker-custom"
//                     />
//                   </div>

//                   {/* Selected Dates Display */}
//                   <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md mb-3">
//                     <div className="text-sm">
//                       <span className="font-medium text-gray-700">Selected: </span>
//                       {startDate ? (
//                         <span className="text-black">
//                           {formatDisplayDate(startDate)}
//                           {endDate ? ` to ${formatDisplayDate(endDate)}` : ' (Select end date)'}
//                         </span>
//                       ) : (
//                         <span className="text-gray-500">Select start date</span>
//                       )}
//                     </div>
//                   </div>

//                   {/* Action Buttons */}
//                   <div className="flex justify-end space-x-2">
//                     <button
//                       onClick={handleClearDates}
//                       className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
//                     >
//                       Clear
//                     </button>
//                     <button
//                       onClick={handleApplyDates}
//                       className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800 transition-colors"
//                       disabled={!startDate || !endDate}
//                     >
//                       Apply
//                     </button>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Factors Section */}
//           <div className="mb-6">
//             <h4 className="text-lg font-semibold text-black mb-4">Select Factors</h4>
//             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//               {pricingTemplate && roomType && availableFactors.length > 0 ? (
//                 availableFactors.map((factor) => (
//                   <label
//                     key={factor.factorname}
//                     className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
//                   >
//                     <input
//                       type="checkbox"
//                       checked={selectedFactorNames.includes(factor.factorname)}
//                       onChange={() => toggleFactorSelection(factor.factorname)}
//                       className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
//                     />
//                     <span className="text-sm text-black">
//                       {factor.factorname}{' '}
//                       <span className="text-gray-600">({factor.defaultpercentage}%)</span>
//                     </span>
//                   </label>
//                 ))
//               ) : (
//                 <div className="col-span-full text-center py-8">
//                   <p className="text-gray-500 text-sm">
//                     {!pricingTemplate || !roomType
//                       ? 'Please select Room Type and Pricing Template to view factors'
//                       : 'No factors available for the selected room type and template.'}
//                   </p>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Action Buttons */}
//         <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 mt-auto">
//           <button
//             className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
//             onClick={handlePricingClick}
//           >
//             Save Pricing
//           </button>
//           <button
//             className="px-6 py-2 bg-white text-black border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm"
//             onClick={handlePreviewClick}
//           >
//             Preview
//           </button>
//           <button
//             className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium text-sm"
//             onClick={handleDeleteClick}
//           >
//             Delete
//           </button>
//         </div>
//       </div>

//       {/* Preview Modal */}
//       {previewData && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//           <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 border border-gray-200">
//             <div className="p-6 border-b border-gray-200">
//               <h2 className="text-lg font-semibold text-black">
//                 {roomType === 'All' ? 'Pricing Preview - All Rooms' : 'Pricing Preview'}
//               </h2>
//             </div>

//             <div className="p-6 space-y-4">
//               <div className="text-sm text-gray-600 space-y-2">
//                 <p>
//                   <span className="font-medium text-black">Date Range:</span> {previewData[0].date}{' '}
//                   to {previewData[previewData.length - 1].date}
//                 </p>
//                 {roomType !== 'All' && (
//                   <p>
//                     <span className="font-medium text-black">Room Type:</span>{' '}
//                     {previewData[0].roomtypename}
//                   </p>
//                 )}
//                 <p>
//                   <span className="font-medium text-black">Template:</span>{' '}
//                   {previewData[0].template}
//                 </p>
//               </div>

//               {roomType === 'All' ? (
//                 <div className="grid grid-cols-2 gap-4">
//                   {Object.entries(
//                     previewData.reduce((acc, roomData) => {
//                       if (!acc[roomData.roomtypename]) {
//                         acc[roomData.roomtypename] = {
//                           roomtypename: roomData.roomtypename,
//                           baseprice: roomData.baseprice,
//                           dynamicprice: roomData.dynamicprice,
//                         };
//                       }
//                       return acc;
//                     }, {})
//                   ).map(([roomType, roomData]) => (
//                     <div
//                       key={roomType}
//                       className="p-3 bg-gray-50 rounded-md border border-gray-200"
//                     >
//                       <h3 className="text-sm font-semibold text-black mb-2">
//                         {roomData.roomtypename}
//                       </h3>
//                       <div className="text-xs text-gray-600 space-y-1">
//                         <p>
//                           <span className="font-medium">Base Price:</span> ₹{roomData.baseprice}
//                         </p>
//                         <p>
//                           <span className="font-medium">Dynamic Price:</span> ₹
//                           {roomData.dynamicprice}
//                         </p>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
//                   <div className="text-sm text-gray-600 space-y-2">
//                     <p>
//                       <span className="font-medium text-black">Base Price:</span> ₹
//                       {previewData[0].baseprice}
//                     </p>
//                     <p>
//                       <span className="font-medium text-black">Dynamic Price:</span> ₹
//                       {previewData[0].dynamicprice}
//                     </p>
//                   </div>
//                 </div>
//               )}
//             </div>

//             <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
//               <div className="flex justify-end">
//                 <button
//                   className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
//                   onClick={() => setPreviewData(null)}
//                 >
//                   Close
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer className="mt-12" />
//     </div>
//   );
// };

// export default Pricing;
